import { Gender as GenderEnum } from '@constants/common.constants';
import { phoneNumberTools } from '@utils/tools/phoneNumberTools';
import { Gender } from '../constants/voterRegistrationConstants';
import { RegistrationReason } from '../constants/voterRegistrationConstants';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';

export const getMappedInitData = (initData) => {
    const {
        firstName,
        middleName,
        lastName,
        titleSuffix,
        unitedStatesCitizen,
        dateOfBirth,
        gender,
        phone,
        email,
        streetAddress,
        streetAddress2,
        city,
        zipcode,
        county,
        mailingAddressSameAsHomeAddressIndicator: isMailingSame,
        mailingAddress,
        mailingCity,
        mailingState,
        mailingZipcode,
    } = initData;

    const mailingFields = isMailingSame
        ? { addressLine: '', city: '', state: '', zipCode: '', county: '' }
        : {
            addressLine: mailingAddress ?? '',
            city: mailingCity ?? '',
            state: mailingState ?? '',
            zipCode: mailingZipcode ?? '',
        };

    return {
        firstName: firstName ?? '',
        middleName: middleName ?? '',
        lastName: lastName ?? '',
        suffix: titleSuffix ?? '',
        dateOfBirth: dateOfBirth ?? '',
        gender:
            gender === GenderEnum.MALE ? Gender.MALE : gender === GenderEnum.FEMALE ? Gender.FEMALE : Gender.UNKNOWN,
        primaryPhone: phone ?? '',
        primaryEmail: email ?? '',
        'residentialAddress.addressLine': streetAddress ?? '',
        'residentialAddress.addressLine2': streetAddress2 ?? '',
        'residentialAddress.city': city ?? '',
        'residentialAddress.zipCode': zipcode ?? '',
        'residentialAddress.county': county ?? '',
        'mailingAddress.isMailingSameAsResidential': isMailingSame ?? false,
        ...Object.fromEntries(Object.entries(mailingFields).map(([k, v]) => [`mailingAddress.${k}`, v])),
    };
};

const getString = (val: unknown): string => (typeof val === 'string' ? val : '') ?? '';
const getBool = (val: unknown): boolean => !!val;

export const mapFormDataToApiFormat = (formData: IVoterRegistrationFormValues) => {
    const {
        firstName,
        middleName,
        lastName,
        suffix,
        isUSCitizen,
        isAgeAbove18yr,
        registrationReason,
        dateOfBirth,
        gender,
        race,
        primaryPhone,
        primaryEmail,
        residentialAddress,
        mailingAddress,
        identification,
        politicalParty,
        otherPoliticalParty,
        votingAssistance,
        votingInformationChanged,
        declaration,
        helpWithForm,
        pollWorker,
        transferRequest,
    } = formData;

    return {
        // Personal Info
        firstName: getString(firstName),
        middleName: getString(middleName),
        lastName: getString(lastName),
        titleSuffix: getString(suffix),
        unitedStatesCitizen: getBool(isUSCitizen),
        eighteenOnElectionDay: getBool(isAgeAbove18yr),

        // Registration Reasons
        isNewRegistration: registrationReason?.includes(RegistrationReason.NEW_REGISTRATION) ?? false,
        nameUpdate: registrationReason?.includes(RegistrationReason.CHANGE_OF_NAME) ?? false,
        addressUpdate: registrationReason?.includes(RegistrationReason.CHANGE_OF_ADDRESS) ?? false,
        isPartyChange: registrationReason?.includes(RegistrationReason.CHANGE_OF_PARTY) ?? false,
        isFederalVoter: registrationReason?.includes(RegistrationReason.FEDERAL_VOTER) ?? false,

        // Demographics
        dateOfBirth: getString(dateOfBirth),
        gender: getString(gender),
        ethnicity: getString(race),
        phone: phoneNumberTools.formatPhoneNumber(primaryPhone) ?? '',
        email: getString(primaryEmail),

        // Residential Address
        streetAddress: getString(residentialAddress?.addressLine),
        streetAddress2: getString(residentialAddress?.addressLine2),
        unitNumber: getString(residentialAddress?.unitNo),
        unitType: getString(residentialAddress?.unitType),
        city: getString(residentialAddress?.city),
        zipcode: getString(residentialAddress?.zipCode),
        county: getString(residentialAddress?.county),

        // Mailing Address
        mailingAddress: getString(mailingAddress?.addressLine),
        mailingCity: getString(mailingAddress?.city),
        mailingState: getString(mailingAddress?.state),
        mailingZipcode: getString(mailingAddress?.zipCode),

        // ID Info
        driversLicense: getString(identification?.driverLicenseOrPennDotId),
        ssn4: getString(identification?.last4digitsSSN),
        donthavebothDLandSSN: getBool(identification?.driverLicenseOrSSNNotAvailable),

        // Political Party
        politicalParty: getString(politicalParty),
        otherPoliticalParty: getString(otherPoliticalParty),

        // Voting Help
        needHelpToVote: votingAssistance?.helpRequired !== null ? getBool(votingAssistance?.helpRequired) : null,
        typeOfAssistance: getString(votingAssistance?.kindOfAssistance),
        preferredLanguage: getString(votingAssistance?.preferredLanguage),

        // Previous Registration Info
        voterRegNumber: getString(votingInformationChanged?.registrationNumber),
        previousRegLastName: getString(votingInformationChanged?.previousLastName),
        previousRegFirstName: getString(votingInformationChanged?.previousFirstName),
        previousRegAddress: getString(votingInformationChanged?.address),
        previousRegCity: getString(votingInformationChanged?.city),
        previousRegState: getString(votingInformationChanged?.state),
        previousRegZip: getString(votingInformationChanged?.zip),
        previousRegCounty: getString(votingInformationChanged?.county),
        previousRegYear: getString(votingInformationChanged?.year),

        // Declaration & Assistance
        declaration1: getBool(declaration),
        assistedPersonName: getString(helpWithForm?.name),
        assistedPersonAddress: getString(helpWithForm?.address),
        assistedPersonPhone: phoneNumberTools.formatPhoneNumber(helpWithForm?.phone) ?? '',
        assistanceDeclaration2: getBool(helpWithForm?.confirmation),

        // Poll Worker Info
        isPollWorker: pollWorker?.isWorker !== null ? getBool(pollWorker?.isWorker) : null,
        bilingualInterpreter: pollWorker?.isBilingual !== null ? getBool(pollWorker?.isBilingual) : null,
        pollWorkerSpeakLang: getString(pollWorker?.language),

        // Misc
        secondEmail: '',
        isTransferPermanent: getBool(transferRequest),
    };
};
